/**
 * UI06a: Map Tab Content
 * Empty state + boundary imported state for the Map tab
 */

import './MapTabContent.css'
import type { TabContentRenderer } from './LeftPanel'
import type { ParcelResult } from '../geodata/types'
import { MapModal } from './MapModal'
import { importParcelAsBoundary } from '../geodata/boundaryImport'
import { clearGeoAnchor, getGeoAnchor } from '../geodata/geoAnchorState'
import { getParcel, clearParcel, getParcels, setParcels } from '../geodata/parcelState'
import { startWalkthrough } from './Walkthrough'
import { t } from '../i18n'

export type MapButtonState = 'idle' | 'loading' | 'success' | 'error'

export class MapTabContent implements TabContentRenderer {
  private container: HTMLElement | null = null
  private mapModal: MapModal | null = null
  private currentParcel: ParcelResult | null = null
  private mapBgState: MapButtonState = 'idle'
  private importBuildingsState: MapButtonState = 'idle'
  private importBuildingsProgress: number = 0

  // Callbacks for external integration
  public onBoundaryImported: ((parcel: ParcelResult, schemeOutline: {
    x: number
    y: number
    points: Array<{ x: number; y: number }>
  }) => void) | null = null
  public onBoundaryCleared: (() => void) | null = null
  public onGetMapBackground: (() => void) | null = null
  public onClearMapBackground: (() => void) | null = null
  public onImportBuildings: (() => void) | null = null
  /** Track whether a map background image is currently loaded */
  private hasMapBackground: boolean = false

  render(container: HTMLElement): void {
    this.container = container

    // Sync from shared parcelState (may have been restored on garden load)
    if (!this.currentParcel) {
      const saved = getParcel()
      if (saved) {
        // Build a minimal ParcelResult from metadata for display
        this.currentParcel = {
          id: saved.id,
          area: saved.area,
          region: saved.region,
          address: saved.address,
          boundary: [],
          boundaryPl2000: [],
        }
      }
    }

    if (this.currentParcel) {
      this.renderImportedState()
    } else {
      this.renderEmptyState()
    }
  }

  destroy(): void {
    if (this.mapModal) {
      this.mapModal.destroy()
      this.mapModal = null
    }
    this.container = null
  }

  onActivate(): void {
    // Check if we have an anchor (boundary was imported)
    const anchor = getGeoAnchor()
    if (!anchor && this.currentParcel) {
      // Anchor was cleared externally, reset state
      this.currentParcel = null
      this.hasMapBackground = false
      if (this.container) {
        this.renderEmptyState()
      }
      return
    }

    // Sync from shared parcelState (may have been restored on garden load)
    if (!this.currentParcel) {
      const saved = getParcel()
      if (saved) {
        this.currentParcel = {
          id: saved.id,
          area: saved.area,
          region: saved.region,
          address: saved.address,
          boundary: [],
          boundaryPl2000: [],
        }
        if (this.container) {
          this.renderImportedState()
        }
      }
    }
  }

  onDeactivate(): void {
    // Nothing to do
  }

  // ============ Public Methods ============

  setParcel(parcel: ParcelResult | null): void {
    this.currentParcel = parcel
    if (this.container) {
      if (parcel) {
        this.renderImportedState()
      } else {
        this.renderEmptyState()
      }
    }
  }

  getParcel(): ParcelResult | null {
    return this.currentParcel
  }

  /**
   * Set whether a map background is currently loaded (shows/hides clear button)
   */
  setHasMapBackground(has: boolean): void {
    this.hasMapBackground = has
    if (this.container && this.currentParcel) {
      this.renderImportedState()
    }
  }

  /**
   * Update the map background button state
   */
  setMapBgState(state: MapButtonState): void {
    this.mapBgState = state
    if (this.container && this.currentParcel) {
      this.renderImportedState()
    }
    // Auto-reset success/error after delay
    if (state === 'success' || state === 'error') {
      setTimeout(() => {
        if (this.mapBgState === state) {
          this.mapBgState = 'idle'
          if (this.container && this.currentParcel) {
            this.renderImportedState()
          }
        }
      }, 3000)
    }
  }

  /**
   * Update the import buildings button state and progress
   */
  setImportBuildingsState(state: MapButtonState, progressOrCount?: number): void {
    this.importBuildingsState = state
    if (progressOrCount !== undefined) {
      this.importBuildingsProgress = progressOrCount
    }
    if (this.container && this.currentParcel) {
      this.renderImportedState()
    }
    // Auto-reset success/error after delay
    if (state === 'success' || state === 'error') {
      setTimeout(() => {
        if (this.importBuildingsState === state) {
          this.importBuildingsState = 'idle'
          this.importBuildingsProgress = 0
          if (this.container && this.currentParcel) {
            this.renderImportedState()
          }
        }
      }, 3000)
    }
  }

  // ============ Private Methods ============

  private renderEmptyState(): void {
    if (!this.container) return

    this.container.innerHTML = `
      <div class="map-tab-content empty-state">
        <div class="map-tab-hero">
          <div class="hero-icon">
            <i class="fa-solid fa-map-location-dot"></i>
          </div>
          <h3 class="hero-title">
            ${t('map.tab.title', 'Property Boundary')}
            <button class="tab-help-btn" data-walkthrough="map" title="${t('tooltip.help', 'Help')}">
              <i class="fa-solid fa-circle-question"></i>
            </button>
          </h3>
          <p class="hero-description">
            ${t('map.tab.description', 'Import your property boundary to accurately position your garden design.')}
          </p>
        </div>

        <div class="map-tab-actions">
          <button class="action-btn primary" data-action="import">
            <i class="fa-solid fa-download"></i>
            ${t('map.tab.importGeoportal', 'Import from Geoportal')}
          </button>
          <button class="action-btn secondary disabled" disabled>
            <i class="fa-solid fa-pen"></i>
            ${t('map.tab.drawManually', 'Draw manually')}
            <span class="badge">${t('common.comingSoon', 'Coming soon')}</span>
          </button>
        </div>

        <div class="map-tab-hint">
          <i class="fa-solid fa-circle-info"></i>
          <span>${t('map.tab.hint', 'Uses Polish land registry (ULDK) data')}</span>
        </div>
      </div>
    `

    // Setup event listeners
    const importBtn = this.container.querySelector('[data-action="import"]')
    importBtn?.addEventListener('click', () => this.openImportModal())
    this.container.querySelector('.tab-help-btn')?.addEventListener('click', () => startWalkthrough('map'))
  }

  private renderImportedState(): void {
    if (!this.container || !this.currentParcel) return

    const parcel = this.currentParcel
    const area = parcel.area.toLocaleString('pl-PL', { maximumFractionDigits: 0 })
    const address = parcel.address || t('map.tab.locatedViaMap', 'Located via map')
    const region = `${parcel.region.commune}, ${parcel.region.county}`
    const sources = getParcels()
    const isMerged = sources.length > 1

    // Map background button state
    const mapBgDisabled = this.mapBgState === 'loading'
    const mapBgIcon = this.mapBgState === 'loading' ? 'fa-spinner fa-spin'
      : this.mapBgState === 'success' ? 'fa-check'
      : this.mapBgState === 'error' ? 'fa-exclamation-triangle'
      : 'fa-image'
    const mapBgLabel = this.mapBgState === 'loading' ? t('map.tab.getMapBackgroundLoading', 'Downloading map...')
      : this.mapBgState === 'success' ? t('map.tab.getMapBackgroundSuccess', 'Map background loaded')
      : this.mapBgState === 'error' ? t('map.tab.getMapBackgroundError', 'Failed to load map')
      : t('map.tab.getMapBackground', 'Get map background')
    const mapBgClass = this.mapBgState === 'success' ? 'action-btn success-state'
      : this.mapBgState === 'error' ? 'action-btn danger'
      : 'action-btn secondary'

    // Import buildings button state
    const importDisabled = this.importBuildingsState === 'loading'
    const importIcon = this.importBuildingsState === 'loading' ? 'fa-spinner fa-spin'
      : this.importBuildingsState === 'success' ? 'fa-check'
      : this.importBuildingsState === 'error' ? 'fa-exclamation-triangle'
      : 'fa-building'
    const importLabel = this.importBuildingsState === 'loading'
      ? t('map.tab.importBuildingsScanning', 'Scanning... {progress}%').replace('{progress}', String(this.importBuildingsProgress))
      : this.importBuildingsState === 'success' ? t('map.tab.importBuildingsSuccess', '{count} buildings imported').replace('{count}', String(this.importBuildingsProgress))
      : this.importBuildingsState === 'error' ? t('map.tab.importBuildingsError', 'Failed to import buildings')
      : t('map.tab.importBuildings', 'Import buildings')
    const importClass = this.importBuildingsState === 'success' ? 'action-btn success-state'
      : this.importBuildingsState === 'error' ? 'action-btn danger'
      : 'action-btn secondary'

    this.container.innerHTML = `
      <div class="map-tab-content imported-state">
        <div class="map-tab-success-header">
          <i class="fa-solid fa-check-circle"></i>
          <span class="success-title">${t('map.tab.title', 'Property Boundary')}</span>
          <button class="tab-help-btn" data-walkthrough="map" title="${t('tooltip.help', 'Help')}">
            <i class="fa-solid fa-circle-question"></i>
          </button>
        </div>

        <div class="map-tab-info">
          <div class="info-row">
            <span class="info-label">${isMerged ? t('map.tab.parcelIds', 'Parcel IDs') : t('map.tab.parcelId', 'Parcel ID')}</span>
            <span class="info-value">${isMerged ? sources.map(p => p.id).join(', ') : parcel.id}</span>
          </div>
          ${isMerged ? `<div class="info-row">
            <span class="info-label">${t('map.tab.mergedParcels', '{count} parcels merged').replace('{count}', String(sources.length))}</span>
            <span class="info-value"></span>
          </div>` : ''}
          <div class="info-row">
            <span class="info-label">${t('map.tab.area', 'Area')}</span>
            <span class="info-value">${area} m²</span>
          </div>
          <div class="info-row">
            <span class="info-label">${t('map.tab.address', 'Address')}</span>
            <span class="info-value ${!parcel.address ? 'muted' : ''}">${address}</span>
          </div>
          <div class="info-row">
            <span class="info-label">${t('map.tab.region', 'Region')}</span>
            <span class="info-value">${region}</span>
          </div>
        </div>

        <div class="map-tab-actions">
          <button class="${mapBgClass}" data-action="map-bg" ${mapBgDisabled ? 'disabled' : ''}>
            <i class="fa-solid ${mapBgIcon}"></i>
            ${mapBgLabel}
          </button>
          ${this.hasMapBackground ? `
          <button class="action-btn secondary" data-action="clear-map">
            <i class="fa-solid fa-xmark"></i>
            ${t('map.tab.clearMapBackground', 'Clear map background')}
          </button>
          ` : ''}
          <button class="${importClass}" data-action="import-buildings" ${importDisabled ? 'disabled' : ''}>
            <i class="fa-solid ${importIcon}"></i>
            ${importLabel}
          </button>
          <button class="action-btn danger" data-action="clear">
            <i class="fa-solid fa-trash"></i>
            ${t('map.tab.clearBoundary', 'Clear boundary')}
          </button>
        </div>
      </div>
    `

    // Setup event listeners
    const mapBgBtn = this.container.querySelector('[data-action="map-bg"]')
    mapBgBtn?.addEventListener('click', () => this.onGetMapBackground?.())
    const clearMapBtn = this.container.querySelector('[data-action="clear-map"]')
    clearMapBtn?.addEventListener('click', () => this.onClearMapBackground?.())
    const importBtn = this.container.querySelector('[data-action="import-buildings"]')
    importBtn?.addEventListener('click', () => this.onImportBuildings?.())
    const clearBtn = this.container.querySelector('[data-action="clear"]')
    clearBtn?.addEventListener('click', () => this.clearBoundary())
    this.container.querySelector('.tab-help-btn')?.addEventListener('click', () => startWalkthrough('map'))
  }

  private openImportModal(): void {
    this.mapModal = new MapModal()
    this.mapModal.onImport = (parcel) => this.handleImport(parcel)
    this.mapModal.onClose = () => {
      this.mapModal = null
    }
    this.mapModal.open()
  }

  private handleImport(parcel: ParcelResult): void {
    // Calculate geo-anchor and transform boundary
    const { schemeOutline } = importParcelAsBoundary(parcel)

    // Store parcel
    this.currentParcel = parcel

    // If this is a merged parcel (composite ID with '+'), store source parcel metadata
    if (parcel.id.includes('+')) {
      const ids = parcel.id.split('+')
      setParcels(ids.map(id => ({
        id,
        area: 0, // Individual areas not tracked after merge
        region: parcel.region,
      })))
    }

    // Re-render with imported state
    if (this.container) {
      this.renderImportedState()
    }

    // Notify external handlers
    this.onBoundaryImported?.(parcel, schemeOutline)
  }

  private clearBoundary(): void {
    // Clear geo-anchor state
    clearGeoAnchor()

    // Clear parcel (both local and shared state)
    clearParcel()
    this.currentParcel = null

    // Re-render empty state
    if (this.container) {
      this.renderEmptyState()
    }

    // Notify external handlers
    this.onBoundaryCleared?.()
  }
}
